import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { useParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import { format } from 'date-fns';
import createNumberMask from 'text-mask-addons/dist/createNumberMask';
import { get, isEmpty } from 'lodash';

import {
  newCheckInAction,
  newNextAction,
} from '../../actions/navigation';
import Modal from '../../components/Modal';
import Header from '../../components/Header';
import ContentWrapper from '../../components/ContentWrapper';
import FormRow from '../../components/Form/FormRow';
import FormColumn from '../../components/Form/FormColumn';
import Footer from '../../components/Footer';
import Pagination from '../../components/Pagination';
import Input from '../../components/Form/Input';
import Select from '../../components/Form/Select';
import Checkbox from '../../components/Form/Checkbox';
import Switch from '../../components/Form/Switch';
import Validator from '../../components/Validator';
import Button from '../../components/Button';
import Transition from '../../components/Transition';
import Container from '../../components/Container';
import Progress from '../../components/Progress';
import Headline from '../../components/Headline';

import { formatCurrency } from '../../utils/formatCurrency';
import { toTitleCase } from '../../utils/toTitleCase';
import { floatUnmask } from '../../utils/unmasks';
import { addLusaHeaderParam, parseUrlParams } from '../../utils/parseUrlParams';
import { EMPLOYMENT_YEARS } from '../../utils/appConstants';
import schema from './loanDetailsSchema';

import imgError from '../../assets/images/error.svg';
import './style.scss';

const currencyMask = createNumberMask({
  prefix: '$ ',
  allowDecimal: true,
});

const unmask = (val) => val.replace(/[$, ]+/g, '');

const switchOptions = ['Rent', 'Own'];
const LoanDetails = (props) => {
  const [isProgressVisible, setIsProgressVisible] = useState(false);
  const [isPopupVisible, setIsPopupVisible] = useState(false);
  const [uploadingPercentage, setUploadingPercentage] = useState(0);
  const match = useParams();
  const { navigate, checkinData, validator: { values, errors, setValues } } = props;
  const employmentStatus = get(values, 'employmentStatus');
  const isEmploymentInfoRequired = !(employmentStatus === 'retired' || employmentStatus === 'unemployed' || employmentStatus === 'student');
  const workflowType = get(match, 'workflowType') === 'luwa' ? 'dtm-luwa' : get(match, 'workflowType');

  useEffect(() => {
    if (!isEmploymentInfoRequired) {
      const formData = {
        ...values,
        employmentYears: '',
        employerName: '',
        employerPhone: '',
      };
      setValues(formData);
    }
  }, [isEmploymentInfoRequired]);

  useEffect(() => {
    const params = parseUrlParams(window.location.search);
    if (isEmpty(checkinData)) {
      props.newCheckInAction({
        workflowType,
        key: get(params, 'key'),
        data: params.test ? { test: params.test } : {},
        success: (res) => {
          let routeUrl = get(res, 'data.url');
          if (routeUrl && params.lusa_header && params.lusa_header === 'false') {
            routeUrl = addLusaHeaderParam(routeUrl);
          }
          navigate(routeUrl);
        },
      });
    }
  }, []);

  const getCurrentDate = (date) => format(date, 'M/DD/YYYY');

  useEffect(() => {
    const initialFormData = {
      loanAmount: `${get(checkinData, 'data.data.loanAmount')}`,
      dateOfService: getCurrentDate(get(checkinData, 'data.data.dateOfService.date')),
      rentOrOwn: 'off',
    };
    setValues(initialFormData);
  }, [checkinData, setValues]);

  const handleSwitchChange = (e) => {
    const { validator: { onChangeHandler } } = props;
    onChangeHandler(e.target.name, e.target.value === 'off' ? 'on' : 'off');
  };

  const handleInputChange = (event) => {
    const { validator: { onChangeHandler } } = props;
    onChangeHandler(event.target.name, event.target.value);
  };

  const handleCheckboxChange = (name, value) => {
    const { validator: { onChangeHandler } } = props;
    onChangeHandler(name, value);
  };

  const handleBlur = (event) => {
    const { validator: { onChangeHandler } } = props;
    switch (event.target.name) {
      case 'employerName':
        onChangeHandler(event.target.name, toTitleCase(event.target.value));
        break;
      default:
        onChangeHandler(event.target.name, event.target.value ? formatCurrency(floatUnmask(event.target.value), 2) : '');
    }
  };

  const handleSubmitFrom = (e) => {
    e.preventDefault();
    const { validator: { validate } } = props;
    const params = parseUrlParams(window.location.search);

    if (validate(schema).isValid) {
      const formData = params.test
        ? {
          loanAmount: unmask(values.loanAmount),
          grossAnnualIncome: unmask(values.annualGrossIncome || 0),
          monthlyRentOrMortage: unmask(values.monthlyRent || 0),
          rentOrOwn: values.rentOrOwn === 'off' ? 'rent' : 'own',
          employmentStatus: values.employmentStatus,
          employerName: values.employerName,
          employerPhone: values.employerPhone,
          employmentYears: values.employmentYears,
          test: params.test,
        } : {
          loanAmount: unmask(values.loanAmount),
          grossAnnualIncome: unmask(values.annualGrossIncome || 0),
          monthlyRentOrMortage: unmask(values.monthlyRent || 0),
          rentOrOwn: values.rentOrOwn === 'off' ? 'rent' : 'own',
          employmentStatus: values.employmentStatus,
          employerName: values.employerName,
          employerPhone: values.employerPhone,
          employmentYears: values.employmentYears,
        };
      setIsProgressVisible(true);
      props.newNextAction({
        workflowType,
        key: get(params, 'key'),
        data: formData,
        onUploadProgress: (progressEvent) => {
          setUploadingPercentage(Math.floor((progressEvent.loaded * 100) / progressEvent.total));
        },
        success: (res) => {
          let routeUrl = get(res, 'data.url');
          if (routeUrl && params.lusa_header && params.lusa_header === 'false') {
            routeUrl = addLusaHeaderParam(routeUrl);
          }
          navigate(routeUrl);
        },
        fail: (err) => {
          console.log(err);
          setIsPopupVisible(true);
          setIsProgressVisible(false);
        },
      });
    }
  };

  const handleClosePopup = () => {
    setIsPopupVisible(false);
  };

  return (
    <Transition className="transition-item wrapper">
      <Header navigate={navigate} />
      <ContentWrapper>
        <Headline
          header={(
            <>
              Verify your
              {' '}
              <span>loan details</span>
              {' '}
              and provide some
              {' '}
              <span>financial info</span>
              .
            </>
          )}
          title={(
            <span className="boldText">
              All stated income and/or employment
              {' '}
              <u>will require verification</u>
              {' '}
              prior to the approval of your loan.
            </span>
          )}
        />
        <Container className="container">
          <FormRow>
            <FormColumn>
              <Input
                label="Loan Amount"
                name="loanAmount"
                tooltip="If total loan amount is incorrect, please contact your provider to have this modified."
                isMasked={currencyMask}
                value={isEmpty(values.loanAmount) ? `${formatCurrency(values.loanAmount || 0, 2)}` : ''}
                onChange={handleInputChange}
                readOnly
              />
            </FormColumn>
            <FormColumn>
              <Input
                label="Date of Service"
                tooltip="If Date of Service is incorrect, please contact your provider to have this modified."
                offset={2}
                value={isEmpty(values.dateOfService) ? `${values.dateOfService}` : ''}
                readOnly
              />
            </FormColumn>
          </FormRow>
          <FormRow>
            <FormColumn>
              <Input
                label="Your Annual Gross Income"
                name="annualGrossIncome"
                tooltip={<>Your total verifiable gross income might include your salary, retirement income or other sources of income. You don&apos;t have to include alimony, child support, or separate maintenance income unless you want it considered as a basis for repayment.</>}
                type="tel"
                isMasked={currencyMask}
                value={values.annualGrossIncome ? `${values.annualGrossIncome}` : ''}
                onChange={handleInputChange}
                hasError={errors.annualGrossIncome}
                onBlur={handleBlur}
                isRequired
              />
            </FormColumn>
          </FormRow>
          <FormRow>
            <FormColumn offset={4}>
              <Switch
                isChecked={values.rentOrOwn}
                onChange={handleSwitchChange}
                options={switchOptions}
                name="rentOrOwn"
              />
            </FormColumn>
            <FormColumn>
              <Input
                label={values.rentOrOwn === 'off' ? 'Monthly Rent' : 'Monthly Mortgage'}
                name="monthlyRent"
                type="tel"
                isMasked={currencyMask}
                value={values.monthlyRent ? `${values.monthlyRent}` : ''}
                onChange={handleInputChange}
                offset={2}
                hasError={errors.monthlyRent}
                onBlur={handleBlur}
                isRequired
              />
            </FormColumn>
          </FormRow>
          <FormRow>
            <FormColumn>
              <Select
                label="Employment Status"
                name="employmentStatus"
                data={[
                  { value: 'employed', title: 'Employed' },
                  { value: 'self-employed', title: 'Self Employed' },
                  { value: 'unemployed', title: 'Unemployed' },
                  { value: 'student', title: 'Student' },
                  { value: 'retired', title: 'Retired' },
                  { value: 'military', title: 'Military' },
                ]}
                value={values.employmentStatus || ''}
                onChange={handleInputChange}
                hasError={errors.employmentStatus}
                isRequired
              />
            </FormColumn>
          </FormRow>
          <FormRow>
            <FormColumn>
              <Input
                label="Employer Name"
                value={values.employerName || ''}
                name="employerName"
                onChange={handleInputChange}
                isRequired={isEmploymentInfoRequired}
                isDisabled={!isEmploymentInfoRequired}
                hasError={errors.employerName}
                onBlur={handleBlur}
              />
            </FormColumn>
          </FormRow>
          <FormRow>
            <FormColumn>
              <Input
                label="Employer Phone"
                value={values.employerPhone || ''}
                isMasked={['(', /[1-9]/, /\d/, /\d/, ')', ' ', /\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/]}
                placeholder={isEmploymentInfoRequired ? '(___) ___-____' : ''}
                type="tel"
                name="employerPhone"
                onChange={handleInputChange}
                isRequired={isEmploymentInfoRequired}
                isDisabled={!isEmploymentInfoRequired}
                hasError={errors.employerPhone}
              />
            </FormColumn>
          </FormRow>
          <FormRow>
            <FormColumn>
              <Select
                label="Years with Current Employer"
                name="employmentYears"
                data={Object.keys(EMPLOYMENT_YEARS).map((key) => ({ title: EMPLOYMENT_YEARS[key], value: key }))}
                value={values.employmentYears || ''}
                onChange={handleInputChange}
                hasError={errors.employmentYears}
                isRequired={isEmploymentInfoRequired}
                isDisabled={!isEmploymentInfoRequired}
              />
            </FormColumn>
          </FormRow>
          <FormRow>
            <FormColumn>
              <Checkbox
                name="consentToVerifyIncomeEmployment"
                label="By checking this box, I hereby authorize LendingUSA to verify my income and employment, which will include contacting my employer if applicable."
                onChange={() => handleCheckboxChange('consentToVerifyIncomeEmployment', !values.consentToVerifyIncomeEmployment)}
                isChecked={values.consentToVerifyIncomeEmployment}
                errorMessage={errors.consentToVerifyIncomeEmployment}
              />
            </FormColumn>
          </FormRow>
        </Container>
      </ContentWrapper>
      <Footer>
        <FormRow>
          <FormColumn>
            <Button
              className="btn arrow"
              onClick={handleSubmitFrom}
              title="Next: Identity Verification"
            />
          </FormColumn>
        </FormRow>
        <FormRow>
          <FormColumn>
            <Pagination activeIndex={1} />
          </FormColumn>
        </FormRow>
      </Footer>
      {
        isPopupVisible && (
          <Modal>
            <div className="card-content error-modal security-error">
              <img src={imgError} alt="" />
              <p>Please fix the errors.</p>
              <div onClick={handleClosePopup} className="btn btn-red">OK</div>
            </div>
          </Modal>
        )
      }
      {
        isProgressVisible && <Progress percent={uploadingPercentage} />
      }
    </Transition>
  );
};

LoanDetails.propTypes = {
  navigate: PropTypes.func.isRequired,
  validator: PropTypes.object.isRequired,
  checkinData: PropTypes.object.isRequired,
  newNextAction: PropTypes.func.isRequired,
  newCheckInAction: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  checkinData: state.screen.checkinData,
});

const mapDispatchToProps = {
  newCheckInAction,
  newNextAction,
};

export default Validator(schema)(connect(mapStateToProps, mapDispatchToProps)(LoanDetails));
